import { useState } from 'react'

import { Content, Portal, Root, Trigger } from '@radix-ui/react-popover'
import { ChevronDown, X } from 'lucide-react'

import { IconCheckboxChecked } from '~/components/ui/icons/checkbox-checked'
import { IconCheckboxEmpty } from '~/components/ui/icons/checkbox-empty'
import { cn } from '~/lib/utils'

type Option = {
  label: string
  value: string
}

interface SimplifiedSelectProps {
  options: Option[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  placeholderIcon?: React.ReactNode
  isClearable?: boolean
  className?: string
  name?: string
  ariaLabel?: string
}

export const SimplifiedSelect = ({
  options,
  value,
  onValueChange,
  placeholder = '選択してください',
  placeholderIcon,
  isClearable = false,
  className,
  name,
  ariaLabel,
}: SimplifiedSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const selectedOption = options.find((opt) => opt.value === value)

  const handleSelect = (optionValue: string) => {
    onValueChange(optionValue)
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onValueChange('')
  }

  return (
    <Root onOpenChange={setIsOpen} open={isOpen}>
      <Trigger asChild>
        <button
          aria-label={ariaLabel}
          className={cn(
            'relative h-[56px] w-full rounded border border-gray-300 text-left',
            'focus:border-[#09909E] focus:ring-2 focus:ring-[#09909E]',
            'transition-colors hover:border-gray-400',
            className,
          )}
          type="button"
        >
          <div className="flex h-full items-center">
            <div className="flex flex-1 items-center px-4">
              {selectedOption ? (
                <div className="flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded bg-[#E6E5E5] px-[6px] py-[3px] text-[85%]">
                  <span>{selectedOption.label}</span>
                </div>
              ) : (
                <div className="flex items-center text-gray-500">
                  {placeholderIcon && (
                    <span className="mr-2 h-4 w-4">{placeholderIcon}</span>
                  )}
                  <span className="pt-1">{placeholder}</span>
                </div>
              )}
            </div>

            {/* 右側のアイコン */}
            <div className="flex flex-shrink-0 items-center gap-1 bg-white pr-2">
              {isClearable && selectedOption && (
                <button
                  aria-label="クリア"
                  className="rounded p-1 hover:bg-gray-200"
                  onClick={handleClear}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </div>
          </div>
        </button>
      </Trigger>

      <Portal>
        <Content
          className="z-[10100] w-[var(--radix-popover-trigger-width)] rounded border border-gray-200 bg-white shadow-lg"
          sideOffset={4}
        >
          <div className="max-h-64 overflow-y-auto">
            {options.map((option) => {
              const isSelected = value === option.value
              return (
                <button
                  className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-100"
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  type="button"
                >
                  {isSelected ? (
                    <IconCheckboxChecked className="h-5 w-5 flex-shrink-0" />
                  ) : (
                    <IconCheckboxEmpty className="h-5 w-5 flex-shrink-0" />
                  )}
                  <span>{option.label}</span>
                </button>
              )
            })}
          </div>
        </Content>
      </Portal>

      {/* Hidden input for form submission */}
      {name && <input name={name} type="hidden" value={value} />}
    </Root>
  )
}
